import { ChevronRightIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

interface PostgridBreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

/**
 * Format URL segments into readable labels
 */
function formatSegmentLabel(segment: string): string {
  const labelMap: Record<string, string> = {
    contacts: 'Contacts',
    'create-contact': 'New Contact',
    correspondence: 'Correspondence',
    cheques: 'Cheques',
    letters: 'Letters',
  };

  // Check if it's a UUID (contact ID)
  if (
    segment.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    )
  ) {
    return 'Contact Details';
  }

  return (
    labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)
  );
}

/**
 * Generate breadcrumbs automatically based on pathname
 */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  // Remove 'postgrid' from segments since that's our home
  const postgridIndex = segments.indexOf('postgrid');
  if (postgridIndex !== -1) {
    segments.splice(postgridIndex, 1);
  }

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const isLast = i === segments.length - 1;

    // Build href for non-current items
    const href = isLast
      ? undefined
      : `/postgrid/${segments.slice(0, i + 1).join('/')}`;

    breadcrumbs.push({
      label: formatSegmentLabel(segment),
      href,
      current: isLast,
    });
  }

  return breadcrumbs;
}

/**
 * PostGrid breadcrumb navigation component
 * Automatically generates breadcrumbs based on current route if no items provided
 */
export const PostgridBreadcrumbs: React.FC<PostgridBreadcrumbsProps> = ({
  items,
  className,
}) => {
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const breadcrumbItems = items || generateBreadcrumbs(location.pathname);

  return (
    <nav className={cn('flex items-center space-x-1', className)}>
      <Link
        to="/postgrid"
        className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
      >
        PostGrid
      </Link>

      {breadcrumbItems.map((item) => (
        <React.Fragment key={item.label}>
          <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          {item.current || !item.href ? (
            <span className="text-sm font-medium text-gray-900">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.href}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {item.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};
